import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Button,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { BRAND_COLORS, alpha } from '../styles/theme';
import {
  FileGridContainer,
  FileCard,
  FileHeader,
  FileMetadata,
  MetadataRow,
  FileIcon,
} from './file-browser/styles';
import { useReportsQuery } from './Reports.queries';
import { useDeleteReport, useDownloadReport } from './Reports.mutations';
import { formatFileSize, formatDate } from './file-browser/utils';
import ReportGenerationDialog from './ReportGenerationDialog';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useTranslation } from '../i18n';
import { ReportInfo } from '../types/api';

interface ReportsViewProps {
  onBack: () => void;
}

const ReportsView: React.FC<ReportsViewProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { showSuccess } = useErrorHandler();
  const [generateDialogOpen, setGenerateDialogOpen] = useState<boolean>(false);
  const [contextMenuAnchor, setContextMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedReport, setSelectedReport] = useState<ReportInfo | null>(null);

  const reportsQuery = useReportsQuery();
  const reports = reportsQuery.data ?? [];
  const downloadReport = useDownloadReport();
  const deleteReport = useDeleteReport();

  const handleDownload = async (report: ReportInfo) => {
    try {
      const downloadUrl = await downloadReport.mutateAsync(report.id);
      window.open(downloadUrl, '_blank');
      showSuccess(t('reports.downloadStarted', { title: report.title }));
    } catch {
      // Error toast is raised by the global mutation handler (lib/queryClient).
    }
  };

  const handleDelete = async (report: ReportInfo) => {
    if (!window.confirm(t('reports.confirmDelete', { title: report.title }))) {
      return;
    }

    try {
      await deleteReport.mutateAsync(report.id);
      showSuccess(t('reports.deleteSuccess', { title: report.title }));
      // The list refreshes automatically via query invalidation in the mutation.
    } catch {
      // Error toast is raised by the global mutation handler (lib/queryClient).
    }
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>, report: ReportInfo) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor(event.currentTarget);
    setSelectedReport(report);
  };

  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
    setSelectedReport(null);
  };

  const handleGenerateSuccess = () => {
    setGenerateDialogOpen(false);
    // The list refreshes automatically via query invalidation in the mutation.
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button onClick={onBack} variant="outlined" size="small">
            {t('reports.backToFiles')}
          </Button>
          <Typography
            variant="h5"
            sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 'bold' }}
          >
            {t('reports.title')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setGenerateDialogOpen(true)}
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.8),
            },
          }}
        >
          {t('reports.generateReport')}
        </Button>
      </Box>

      {reportsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('reports.failedToLoad')}
        </Alert>
      )}

      {reportsQuery.isFetching && <LinearProgress sx={{ mb: 2 }} />}

      {/* Reports Grid */}
      {!reportsQuery.isLoading && reports.length > 0 && (
        <FileGridContainer>
          {reports.map((report) => (
            <FileCard
              key={report.id}
              onClick={() => handleDownload(report)}
              onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => handleContextMenu(e, report)}
            >
              <FileHeader>
                <FileIcon
                  className="file"
                  style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)' }}
                >
                  PDF
                </FileIcon>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {report.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {report.summary.total_flight_hours.toFixed(1)} hrs |{' '}
                    {report.summary.total_flights} flights
                  </Typography>
                </Box>
              </FileHeader>

              <FileMetadata>
                <MetadataRow>
                  <Chip
                    label={`${report.summary.drones_count} drones`}
                    size="small"
                    sx={{
                      backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
                      color: BRAND_COLORS.forestGreen.main,
                      height: '20px',
                      fontSize: '0.7rem',
                    }}
                  />
                  {report.summary.incidents_count > 0 && (
                    <Chip
                      label={`${report.summary.incidents_count} incidents`}
                      size="small"
                      sx={{
                        backgroundColor: alpha('#dc2626', 0.1),
                        color: '#dc2626',
                        height: '20px',
                        fontSize: '0.7rem',
                      }}
                    />
                  )}
                </MetadataRow>
                <MetadataRow>
                  <span>{formatDate(report.generated_at)}</span>
                  {report.file_size && <span>{formatFileSize(report.file_size)}</span>}
                </MetadataRow>
                {report.generated_by_name && (
                  <Typography variant="caption" color="text.secondary">
                    By: {report.generated_by_name}
                  </Typography>
                )}
              </FileMetadata>

              {/* Context Menu Button */}
              <Box sx={{ position: 'absolute', top: 2, right: 2, opacity: 0.6 }}>
                <Tooltip title={t('reports.moreActions')}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleContextMenu(e, report);
                    }}
                    sx={{
                      padding: '1px',
                      width: '16px',
                      height: '16px',
                      '&:hover': {
                        backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
                        opacity: 1,
                      },
                    }}
                  >
                    <MoreIcon sx={{ fontSize: '12px' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </FileCard>
          ))}
        </FileGridContainer>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
      >
        {selectedReport && (
          <>
            <MenuItem
              onClick={() => {
                handleDownload(selectedReport);
                handleCloseContextMenu();
              }}
            >
              <DownloadIcon sx={{ mr: 1 }} />
              {t('reports.download')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleDelete(selectedReport);
                handleCloseContextMenu();
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon sx={{ mr: 1 }} />
              {t('reports.delete')}
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Generate Dialog */}
      <ReportGenerationDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        onSuccess={handleGenerateSuccess}
      />
    </Box>
  );
};

export default ReportsView;
